const DICE_TERM = /^(\d*)D(4|6|8|10|12|20|100)(KH|KL)?$/i;
const NUMBER_TERM = /^\d+$/;

export function parserCompatibleNotation(notation) {
  return notation.startsWith('-') ? `0${notation}` : notation;
}

export class DiceInputNotation {
  constructor(terms = []) {
    this.terms = terms;
  }

  static parse(value) {
    const compact = String(value || '').replace(/\s+/g, '');
    const terms = [];

    for (const match of compact.matchAll(/([+-]?)([^+-]+)/g)) {
      const sign = match[1] === '-' ? -1 : 1;
      const body = match[2];
      const die = body.match(DICE_TERM);

      if (die) {
        terms.push({
          type: 'die',
          quantity: sign * Number(die[1] || 1),
          sides: Number(die[2]),
          modifier: die[3]?.toUpperCase() || null
        });
      } else if (NUMBER_TERM.test(body)) {
        terms.push({ type: 'number', value: sign * Number(body) });
      } else {
        terms.push({ type: 'raw', sign, body });
      }
    }

    return new this(terms);
  }

  adjustDie(sides, amount) {
    const numericSides = Number(sides);
    const term = this.terms.find((candidate) => candidate.type === 'die' && candidate.sides === numericSides);

    if (term) {
      term.quantity += Number(amount);
      if (Math.abs(term.quantity) < 2) term.modifier = null;
      if (term.quantity === 0) this.terms.splice(this.terms.indexOf(term), 1);
    } else {
      const firstNumber = this.terms.findIndex((candidate) => candidate.type === 'number');
      const insertionIndex = firstNumber === -1 ? this.terms.length : firstNumber;
      this.terms.splice(insertionIndex, 0, {
        type: 'die',
        quantity: Number(amount),
        sides: numericSides,
        modifier: null
      });
    }

    return this;
  }

  adjustBonus(amount) {
    const value = this.bonus + Number(amount);
    this.terms = this.terms.filter((term) => term.type !== 'number');
    if (value !== 0) this.terms.push({ type: 'number', value });
    return this;
  }

  toggleModifier(sides, modifier) {
    const term = this.terms.find(
      (candidate) => candidate.type === 'die' && candidate.sides === Number(sides) && Math.abs(candidate.quantity) >= 2
    );
    const normalizedModifier = modifier.toUpperCase();
    if (term) term.modifier = term.modifier === normalizedModifier ? null : normalizedModifier;
    return this;
  }

  die(sides) {
    return this.terms.find((term) => term.type === 'die' && term.sides === Number(sides));
  }

  get bonus() {
    return this.terms.filter((term) => term.type === 'number').reduce((sum, term) => sum + term.value, 0);
  }

  toString() {
    return this.terms
      .filter((term) => term.type === 'raw' || this.#termValue(term) !== 0)
      .map((term, index) => this.#renderTerm(term, index))
      .join('');
  }

  #renderTerm(term, index) {
    const value = this.#termValue(term);
    const prefix = value < 0 ? '-' : index === 0 ? '' : '+';

    if (term.type === 'die') {
      return `${prefix}${Math.abs(term.quantity)}d${term.sides}${term.modifier || ''}`;
    }
    if (term.type === 'number') return `${prefix}${Math.abs(term.value)}`;
    return `${term.sign < 0 ? '-' : index === 0 ? '' : '+'}${term.body}`;
  }

  #termValue(term) {
    if (term.type === 'die') return term.quantity;
    if (term.type === 'number') return term.value;
    return term.sign;
  }
}
